<script setup lang="ts">
import {
    faCheck,
    faCircleExclamation,
    faCircleInfo,
    faClose,
    faTriangleExclamation,
} from '@fortawesome/pro-regular-svg-icons';
import type { ButtonSize, IconSize, MessageItem } from '#imports';

interface IProps {
    size?: 'large' | 'small';
    type?: 'error' | 'warning' | 'info' | 'success';
    items?: MessageItem[];
    title?: string;
    message?: string;
    hideIcon?: boolean;
    isClosable?: boolean;
}
const {
    size = 'small',
    items,
    hideIcon,
    isClosable,
    type = 'info',
    title,
    message,
} = defineProps<IProps>();

const emit = defineEmits<{
    close: [];
}>();

const color = computed(() => {
    switch (type) {
        case 'error':
            return 'red';
        case 'info':
            return 'blue';
        case 'success':
            return 'green';
        case 'warning':
            return 'yellow';
        default:
            return '';
    }
});
const fontSize = computed(() => (size === 'large' ? 'medium' : size));
const icon = computed(() => {
    switch (type) {
        case 'warning':
            return faCircleExclamation;
        case 'success':
            return faCheck;
        case 'info':
            return faCircleInfo;
        case 'error':
            return faTriangleExclamation;
        default:
            return undefined;
    }
});
const iconSize = computed<IconSize>(() => {
    switch (size) {
        case 'small':
            return 'medium';
        case 'large':
            return 'large';
        default:
            return 'medium';
    }
});
const closeButtonSize = computed<ButtonSize>(() => {
    switch (size) {
        case 'small':
            return 'small';
        case 'large':
        default:
            return 'medium';
    }
});
const messages = computed<MessageItem[]>(() => {
    if (items) {
        return items;
    }
    if (message) {
        return [
            {
                title,
                message,
            },
        ];
    }
    return [];
});
</script>
<template>
    <div
        class="mr-flash-root"
        :style="{
            backgroundColor: `var(--${color}-200)`,
            borderColor: `var(--${color}-300)`,
        }"
    >
        <div v-for="(item, key) in messages" :key class="mr-flash-item">
            <template v-if="!hideIcon">
                <img
                    v-if="item.iconImg"
                    class="item-icon"
                    :src="item.iconImg"
                    alt="warning"
                />
                <mr-icon
                    v-else-if="icon"
                    :size="iconSize"
                    :icon
                    :color="`var(--${color}-500)`"
                />
            </template>
            <div class="body">
                <mr-typography
                    v-if="item.title"
                    :size="fontSize"
                    weight="700"
                    :color="`var(--${color}-700)`"
                >
                    {{ item.title }}
                </mr-typography>
                <mr-typography
                    :size="fontSize"
                    weight="500"
                    align="justify"
                    :color="`var(--${color}-700)`"
                    v-html="item.message"
                />
            </div>
        </div>
        <mr-button
            v-if="isClosable"
            :icon-button="{ icon: faClose, color: 'var(--gray-700)' }"
            class="close-button"
            variant="transparent"
            :size="closeButtonSize"
            @click="emit('close')"
        />
    </div>
</template>

<style lang="scss" scoped>
.mr-flash-root {
    border-width: 1px;
    border-style: solid;
    display: flex;
    flex-direction: column;
    gap: 8px;
    border-radius: 12px;
    padding: 8px 12px;
    position: relative;
    .mr-flash-item {
        display: flex;
        align-items: center;
        gap: 8px;
        .item-icon {
            width: 18px;
        }
        .body {
            display: flex;
            flex-direction: column;
            flex-grow: 1;
            :deep(b) {
                @include font-weight(700);
            }
        }
    }
}

.close-button {
    position: absolute;
    left: 2px;
    top: 0;
}
</style>
